import styled from 'styled-components'
import { ReactElement, ReactNode, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useFormikContext } from 'formik'
import get from 'lodash/get'
import { Divider, Grid, H, P } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'

import RadioGroup from 'components/form/radio-group'
import { SelectInput } from 'components/form'
import { FuneralBrandId } from 'config/brands'
import { Constants } from 'lib/models/constants'
import { FuneralPlanLeadAttributes } from 'lib/models/lead'
import { formatCelebrationOptions, formatUrnOptions } from 'utils/helpers'

const StyledList = styled.ul`
  list-style: disc;
  padding-left: ${GTR.L};
`

const FORM_FIELDS_NAMES = {
  CELEBRATION_PURCHASE: 'quoteAttributes.celebration.purchase',
  CELEBRATION_TYPE: 'quoteAttributes.celebration.type',
  URN_TYPE: 'quoteAttributes.urnType',
} as const

export type CelebrationFieldNames = {
  CELEBRATION_PURCHASE: string
  CELEBRATION_TYPE: string
  URN_TYPE: string
}

export type CelebrationProps = {
  /** Where the answers live in the surrounding Formik form. Defaults to the
   * funeral plan lead's quoteAttributes. */
  fieldNames?: CelebrationFieldNames
  /** Default to the lead's quoteAttributes.packageSlug and contact.brandId */
  packageSlug?: string
  brandId?: string
  /** Called after each answer changes. Defaults to submitting the form, which
   * saves the lead. */
  onSave?: (change: { name: string; value: string | null }) => void
  /** Disables "Yes, add now" if we do not want to allow celebration purchase */
  isPurchaseNowDisabled?: boolean
  /** Hides the divider at the top of the component */
  hideDivider?: boolean
  /** Shown beneath the celebration type and urn choices when "Yes, add now"
   * is selected */
  children?: ReactNode
}

export const Celebration = ({
  fieldNames = FORM_FIELDS_NAMES,
  packageSlug: packageSlugProp,
  brandId: brandIdProp,
  onSave,
  isPurchaseNowDisabled = false,
  hideDivider = false,
  children,
}: CelebrationProps = {}): ReactElement => {
  const cache = useQueryClient()
  const constants = cache.getQueryData('constants') as Constants
  const { values, handleSubmit, setFieldValue } =
    useFormikContext<FuneralPlanLeadAttributes>()

  const packageSlug =
    packageSlugProp ?? (values.quoteAttributes?.packageSlug as string)
  const brandId = brandIdProp ?? values.contact?.brandId
  const save = (change: { name: string; value: string | null }) =>
    onSave ? onSave(change) : handleSubmit()

  const CELEBRATION_OPTIONS = formatCelebrationOptions({
    packageSlug,
    celebrations: constants?.attributes?.celebrationPrices,
  })

  const [showCelebrationTypeChoice, setShowCelebrationTypeChoice] = useState(
    get(values, fieldNames.CELEBRATION_PURCHASE) === 'yes'
  )

  // Only show the celebration component if there are celebration options to choose from
  if (CELEBRATION_OPTIONS.length === 0) {
    return <></>
  }

  /** This urn is always the first (and only) one on the plan, purchased
   * alongside the celebration, so it's always celebration-priced. */
  const CELEBRATION_URN_OPTIONS = formatUrnOptions({
    brandId: brandId as FuneralBrandId,
    packageSlug,
    urns: constants?.attributes?.urnPrices,
    isFirstUrn: true,
    hasChosenCelebration: true,
  })

  const handleCelebrationChange = ({ value }: { value: string }) => {
    if (value === 'yes') {
      /** If there is but one celebration type option, set it by default */
      if (CELEBRATION_OPTIONS.length === 1) {
        setFieldValue(fieldNames.CELEBRATION_TYPE, CELEBRATION_OPTIONS[0].value)
        save({ name: fieldNames.CELEBRATION_PURCHASE, value })
      }
      setShowCelebrationTypeChoice(true)

      return
    }

    if (['later', 'no'].includes(value)) {
      setShowCelebrationTypeChoice(false)
      /** reset the celebration.type and urnType */
      setFieldValue(fieldNames.CELEBRATION_TYPE, null)
      setFieldValue(fieldNames.URN_TYPE, null)
      save({ name: fieldNames.CELEBRATION_PURCHASE, value })
    }
  }

  return (
    <>
      {!hideDivider && <Divider margin={['L', 0]} />}
      <Grid.Item>
        <H size="S" margin={['XL', 0, 'S']}>
          Add a celebration
        </H>
        <Grid data-testid="add-a-celebration">
          <Grid.Item>
            <P>Would they like to add a gathering at the crematorium?</P>
            <StyledList>
              <li>
                Up to 25 people can attend, lasts 20 minutes and happens before
                10am or after 4:30pm, Monday to Friday
              </li>
              <li>They can pay for this now or add it to their plan later</li>
            </StyledList>
            <RadioGroup
              name={fieldNames.CELEBRATION_PURCHASE}
              handleSave={handleCelebrationChange}
              options={[
                {
                  label: 'Yes, add now',
                  value: 'yes',
                  isOptionDisabled: isPurchaseNowDisabled,
                },
                { label: 'Will consider adding later', value: 'later' },
                { label: 'Not interested', value: 'no' },
              ]}
            />
          </Grid.Item>
          {showCelebrationTypeChoice && (
            <>
              <Grid.Item spanFromL={6} startColumn={1}>
                <SelectInput
                  label="Celebration type"
                  name={fieldNames.CELEBRATION_TYPE}
                  options={CELEBRATION_OPTIONS}
                  handleSave={({ name, value }) =>
                    save({ name, value: value as string })
                  }
                />
              </Grid.Item>
              <Grid.Item spanFromL={6} startColumn={1}>
                <SelectInput
                  label="Choice of urn (included in celebration)"
                  name={fieldNames.URN_TYPE}
                  options={CELEBRATION_URN_OPTIONS}
                  handleSave={({ name, value }) =>
                    save({ name, value: value as string })
                  }
                />
              </Grid.Item>
              {children && <Grid.Item>{children}</Grid.Item>}
            </>
          )}
        </Grid>
      </Grid.Item>
    </>
  )
}
